<script setup lang="ts">
import { ref } from 'vue'
import { healthService } from '@/api/health'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faSyringe,
  faUserMd,
  faCalendarDay,
  faCalendarPlus,
  faBarcode,
  faNotesMedical,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faShieldVirus
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faSyringe,
  faUserMd,
  faCalendarDay,
  faCalendarPlus,
  faBarcode,
  faNotesMedical,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faShieldVirus
)

interface Animal {
  id: string
  species: string
}

const props = defineProps<{
  animal: Animal
}>()

const emit = defineEmits<{
  success: []
}>()

const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const formData = ref({
  vaccine_name: '',
  vaccination_date: new Date().toISOString().split('T')[0],
  next_due_date: '',
  batch_lot_number: '',
  administered_by: '',
  notes: ''
})

async function submitForm() {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    await healthService.createVaccination({
      ...formData.value,
      animal: props.animal.id
    })
    
    successMessage.value = 'Immunization record saved successfully!'
    setTimeout(() => {
      emit('success')
    }, 1500)
  } catch (err: any) {
    errorMessage.value = err.response?.data?.detail || err.message || 'Failed to record immunization'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <form @submit.prevent="submitForm" class="space-y-6">
    <!-- Vaccine Essentials Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-emerald-500 rounded-full"></div>
          Immunization Details
        </h3>
      </div>
      
      <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Vaccine Name -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Vaccine / Product <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="shield-virus" />
            </div>
            <input 
              v-model="formData.vaccine_name" 
              type="text" 
              required 
              placeholder="e.g. Anthrax Spore Vaccine"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Administered By -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Medical Personnel <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="user-md" />
            </div>
            <input 
              v-model="formData.administered_by" 
              type="text" 
              required 
              placeholder="Full name of practitioner"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Vaccination Date -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Administration Date <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="calendar-day" />
            </div>
            <input 
              v-model="formData.vaccination_date" 
              type="date" 
              required 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Next Due Date -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Booster Due Date</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="calendar-plus" />
            </div>
            <input 
              v-model="formData.next_due_date" 
              type="date" 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Batch Number -->
        <div class="col-span-1 md:col-span-2 space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Batch / Lot Identifier</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-slate-600 transition-colors">
              <FontAwesomeIcon icon="barcode" />
            </div>
            <input 
              v-model="formData.batch_lot_number" 
              type="text" 
              placeholder="e.g. BATCH-2024-X9"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-slate-400 focus:ring-4 focus:ring-slate-400/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Notes Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-slate-400 rounded-full"></div>
          Supplemental Information
        </h3>
      </div>
      <div class="p-8">
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Clinical Notes</label>
          <div class="relative group">
            <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="notes-medical" />
            </div>
            <textarea 
              v-model="formData.notes" 
              rows="3" 
              placeholder="Reaction observations, site of injection, etc..." 
              class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Alert Messages -->
    <div class="space-y-3">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="successMessage" class="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="check-circle" class="mr-3 text-lg" />
          {{ successMessage }}
        </div>
      </transition>
      
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="errorMessage" class="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="circle-exclamation" class="mr-3 text-lg" />
          {{ errorMessage }}
        </div>
      </transition>
    </div>

    <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
      <button 
        type="button" 
        @click="$emit('success')" 
        class="px-8 py-3.5 text-slate-400 font-black rounded-2xl hover:bg-slate-50 hover:text-slate-600 transition-all active:scale-95 flex items-center gap-2"
      >
        <FontAwesomeIcon icon="times" />
        Discard
      </button>
      <button 
        type="submit" 
        :disabled="loading"
        class="px-10 py-3.5 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-100 active:scale-95 disabled:bg-slate-200 disabled:shadow-none flex items-center gap-3"
      >
        <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
        <FontAwesomeIcon v-else icon="save" />
        {{ loading ? 'Securing Record...' : 'Confirm Vaccination' }}
      </button>
    </div>
  </form>
</template>
